// Marketing Console — unified view of the Marketing section: waitlist, scheduled
// social, A/B tests, in-app notices. Kit-based, deep-links to each operational tool.
import React, { useState, useEffect } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Bar, Donut, StatLine, Empty, Pill, PALETTE, useConsoleTheme, useIsMobile } from '../components/Console';
import { supabase } from '../supabaseClient';

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());

function useMarketing() {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_marketing_overview').then(({ data }) => { if (!c) setD(data || {}); }); return () => { c = true; }; }, []);
  return d;
}

function LinkBtn({ label, onClick }) {
  const { t } = useConsoleTheme();
  return <button onClick={onClick} style={{ fontSize:'0.85rem', padding:'6px 11px', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500, fontFamily:'inherit' }}>{label} →</button>;
}

function Body({ onNav }) {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const d = useMarketing();
  const col = n => (mobile ? '1fr 1fr' : `repeat(${n},1fr)`);

  const wl = (d && d.waitlist) || {};
  const so = (d && d.social) || {};
  const ab = (d && d.abtests) || {};
  const no = (d && d.notices) || {};

  const wlSources = (wl.by_source || []);
  const maxS = Math.max(1, ...wlSources.map(x => x.n));
  const wlCountries = (wl.by_country || []);
  const maxWC = Math.max(1, ...wlCountries.map(x => x.n));
  const platDonut = (so.by_platform || []).map((x, i) => ({ label: x.k, value: x.n, color: PALETTE[i % PALETTE.length] }));
  const abRows = (ab.rows || []);

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns: col(4), gap:10 }}>
        <KPI label="Waitlist" value={fmt(wl.total)} sub={`${fmt(wl.pending || 0)} pending invite`} subColor={(wl.pending || 0) > 0 ? t.accent : t.mut} accent="#9F7E4F" onClick={() => onNav && onNav('waitlist')} />
        <KPI label="Scheduled posts" value={fmt(so.scheduled)} sub={`${fmt(so.total || 0)} total`} accent="#6F8FA7" onClick={() => onNav && onNav('social')} />
        <KPI label="A/B tests" value={fmt(ab.running)} sub="running now" subColor={(ab.running || 0) > 0 ? t.up : t.mut} accent="#5a8fa6" onClick={() => onNav && onNav('abtest')} />
        <KPI label="Active notices" value={fmt(no.active)} sub={`${fmt(no.total || 0)} total`} accent={t.accent} onClick={() => onNav && onNav('notices')} />
      </div>

      <SectionLabel right="acquisition">Waitlist</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◔ By source" right={<LinkBtn label="Waitlist" onClick={() => onNav && onNav('waitlist')} />}>
          {!d ? <Empty>Loading…</Empty> : (wl.total || 0) === 0 ? <Empty>No waitlist signups yet — sources and geography fill in here as people join.</Empty>
            : wlSources.map(x => <Bar key={x.k} label={x.k} value={fmt(x.n)} pct={Math.round(x.n / maxS * 100)} labelWidth={130} />)}
        </Panel>
        <Panel title="◷ By country">
          {!d ? <Empty>Loading…</Empty> : wlCountries.length === 0 ? <Empty>No country data yet.</Empty>
            : wlCountries.map(x => <Bar key={x.k} label={x.k} value={fmt(x.n)} pct={Math.round(x.n / maxWC * 100)} color={t.accent} labelWidth={130} />)}
          <div style={{ height:1, background:t.bd, margin:'10px 0' }} />
          <StatLine label="Total signups" value={fmt(wl.total)} />
          <StatLine label="Invited" value={fmt(wl.invited)} color={t.up} />
          <StatLine label="Pending" value={fmt(wl.pending)} color={(wl.pending || 0) > 0 ? t.warn : t.mut} />
        </Panel>
      </div>

      <SectionLabel right="distribution">Social & experiments</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◇ Social by platform" right={<LinkBtn label="Composer" onClick={() => onNav && onNav('social')} />}>
          {!d ? <Empty>Loading…</Empty> : (so.total || 0) === 0 ? <Empty>No social posts yet — schedule the first from the composer.</Empty>
            : <Donut data={platDonut} center={{ value: fmt(so.total), label: 'posts' }} />}
        </Panel>
        <Panel title="◈ A/B tests" right={<LinkBtn label="Experiments" onClick={() => onNav && onNav('abtest')} />}>
          {!d ? <Empty>Loading…</Empty> : abRows.length === 0 ? <Empty>No experiments yet — define one to start measuring.</Empty>
            : abRows.slice(0, 8).map((x, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 0', borderBottom:`1px solid ${t.bd}` }}>
                <span style={{ color:t.tx, fontSize:'1rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'58%' }}>{x.name || '—'}</span>
                <span style={{ display:'flex', gap:8, alignItems:'center' }}>
                  {x.metric && <span style={{ fontSize:'0.85rem', color:t.mut }}>{x.metric}</span>}
                  <Pill tone={['running','active','live'].includes((x.status || '').toLowerCase()) ? 'up' : undefined}>{x.status || '—'}</Pill>
                </span>
              </div>
            ))}
        </Panel>
      </div>

      <SectionLabel right="jump to a tool">Marketing tools</SectionLabel>
      <Panel>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <LinkBtn label="Campaigns" onClick={() => onNav && onNav('campaigns')} />
          <LinkBtn label="Brand Kit" onClick={() => onNav && onNav('brandkit')} />
          <LinkBtn label="Waitlist" onClick={() => onNav && onNav('waitlist')} />
          <LinkBtn label="Email" onClick={() => onNav && onNav('email')} />
          <LinkBtn label="Social" onClick={() => onNav && onNav('social')} />
          <LinkBtn label="A/B tests" onClick={() => onNav && onNav('abtest')} />
          <LinkBtn label="SEO" onClick={() => onNav && onNav('seo')} />
          <LinkBtn label="Notices" onClick={() => onNav && onNav('notices')} />
          <LinkBtn label="Push notifications" onClick={() => onNav && onNav('notifications')} />
        </div>
      </Panel>
    </div>
  );
}

export default function MarketingConsole(props) {
  return <ConsoleShell title="Marketing Console"><Body onNav={props.onNav} /></ConsoleShell>;
}
